import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { GrafanaTheme2 } from '@grafana/data';
import { locationService, PluginPage } from '@grafana/runtime';
import { Button, RadioButtonGroup, useStyles2 } from '@grafana/ui';

import { css, cx } from '@emotion/css';

import { useInvestigation, useInvestigationAnalyses } from 'api';
import { PLUGIN_ROOT } from 'consts';
import { useChecks, useFilteredAnalysesByStatus } from 'hooks';
import { Input } from 'types';

import { Actions } from './Actions';
import { CardContainer } from './CardContainer';
import { List } from './List';
import { Timeline } from './Timeline';
import { Title } from './Title';

const Investigation: React.FC = () => {
  const styles = useStyles2(getStyles);
  const { investigationId, analysisId } = useParams<{ investigationId: string; analysisId?: string }>();
  const [filter, setFilter] = useState<string>('interesting');
  const [showTimeline, setShowTimeline] = useState(false);

  // this is logic is a mess
  const { data } = useInvestigation(investigationId!);
  const { data: analyses } = useInvestigationAnalyses(investigationId!); // isError, isLoading, refetch
  const availableAnalyses = useChecks(analyses || []);

  const { categorizedAnalyses } = useFilteredAnalysesByStatus(analyses || [], availableAnalyses);

  const filteredAnalyses = categorizedAnalyses.filter((analysis) => {
    if (filter === 'all') {
      return true;
    }
    return analysis.result.interesting === true;
  });

  const pageTitle = data?.name ?? 'View investigation';

  // when an analysis is selected, put that id into the url
  const onSelection = (analysisId: string) => {
    locationService.push(`${PLUGIN_ROOT}/investigations/${investigationId}/${analysisId}`);
  };

  const inputs: Input[] = (data ?? {}).inputs ?? [];
  const metricInputs = inputs.filter((input) => input.type === 'metric-query');
  const isTimelineAvailable = metricInputs.length > 0;

  return (
    <PluginPage
      pageNav={{ text: pageTitle }}
      subTitle=""
      actions={<Actions investigation={data} />}
      renderTitle={() => <Title title={pageTitle} investigation={data} />}
    >
      <div
        className={showTimeline && isTimelineAvailable ? styles.container : cx([styles.container, styles.hideTimeline])}
      >
        {showTimeline === true ? (
          <div className={styles.timeline}>
            <Timeline investigation={data} analyses={analyses} />
          </div>
        ) : null}
        <div className={styles.filters}>
          <RadioButtonGroup
            disabled={analysisId !== undefined}
            size="md"
            options={[
              {
                label: 'All',
                value: 'all',
              },
              {
                label: 'Interesting',
                value: 'interesting',
              },
            ]}
            onChange={(val) => {
              setFilter(val);
            }}
            value={filter}
          />
          {isTimelineAvailable ? (
            <Button variant="secondary" onClick={() => setShowTimeline(!showTimeline)}>
              {showTimeline ? 'Hide' : 'Show'} Timeline
            </Button>
          ) : null}
        </div>

        <div className={styles.list}>
          <List analyses={filteredAnalyses} selectedAnalysisId={analysisId} onSelection={onSelection} />
        </div>
        <div className={styles.cards}>
          <CardContainer
            investigation={data!}
            analyses={filteredAnalyses}
            selectedAnalysisId={analysisId}
            onSelection={onSelection}
          />
        </div>
      </div>
    </PluginPage>
  );
};

const getStyles = (theme: GrafanaTheme2) => {
  return {
    container: css`
      display: grid;
      width: 100%;
      grid-template-columns: 300px 1fr;
      grid-template-rows: 200px 40px 80%;
      gap: 12px 12px;
      grid-auto-flow: row;
      grid-template-areas:
        'timeline timeline'
        'filters filters'
        'list cards';
    `,
    hideTimeline: css`
      grid-template-rows: 40px 1fr;
      grid-template-areas:
        'filters filters'
        'list cards';
    `,
    timeline: css`
      grid-area: timeline;
      background-color: ${theme.colors.background.secondary};
    `,
    list: css`
      grid-area: list;
    `,
    cards: css`
      grid-area: cards;
    `,
    filters: css`
      grid-area: filters;
      overflow: hidden;
      display: flex;
      justify-content: flex-end;
      background-color: ${theme.colors.background.secondary};
      padding: 4px;
      gap: 12px;
      & label {
        cursor: pointer;
        height: auto;
      }
    `,
  };
};

export default Investigation;
